// src/lib/schemas.ts
import { z } from 'zod';
import {
  MAX_LETTER_CONTENT_LENGTH,
  MAX_LETTER_TITLE_LENGTH,
  MIN_LETTER_CONTENT_LENGTH,
} from './constants';

// ---- 漂流内容 ----

export const throwBottleSchema = z.object({
  content: z
    .string()
    .min(10, '漂流瓶内容至少10字')
    .max(500, '漂流瓶内容不能超过500字'),
  moodTags: z.array(z.string()).max(3, '最多选择3个心情标签').optional(),
  isAnonymous: z.boolean().optional(),
});

export const throwDriftLetterSchema = z.object({
  title: z
    .string()
    .min(1, '请填写漂流信标题')
    .max(100, '标题不能超过100字'),
  content: z
    .string()
    .min(50, '漂流信内容至少50字')
    .max(50000, '漂流信内容不能超过50000字'),
  style: z.enum(['classic', 'modern', 'vintage', 'elegant']).optional(),
  moodTags: z.array(z.string()).max(3, '最多选择3个心情标签').optional(),
  isAnonymous: z.boolean().optional(),
});

// ---- 信使申请 ----

export const applyCourierSchema = z.object({
  name: z
    .string()
    .min(2, '姓名至少2个字符')
    .max(20, '姓名不能超过20字'),
  province_code: z
    .string()
    .length(6, '请选择省份')
    .regex(/^\d{6}$/, '省份代码格式错误'),
  province_name: z
    .string()
    .min(2, '请选择省份'),
  city_code: z
    .string()
    .length(6, '请选择市')
    .regex(/^\d{6}$/, '市代码格式错误'),
  city_name: z
    .string()
    .min(2, '请选择市'),
  district_code: z
    .string()
    .length(6, '请选择区')
    .regex(/^\d{6}$/, '区代码格式错误'),
  district_name: z
    .string()
    .min(2, '请选择区'),
  school_code: z
    .string()
    .length(2, '请选择学校')
    .regex(/^[A-Z0-9]{2}$/, '学校代码格式错误'),
  area_code: z
    .union([
      z.literal(''),
      z
        .string()
        .length(2, '区域代码应为2位')
        .regex(/^[A-Z0-9]{2}$/, '区域代码格式错误'),
    ])
    .optional(),
  transport_method: z
    .enum(['walk', 'bike', 'ebike'])
    .optional(),
  weekly_hours: z
    .number()
    .min(1, '每周可用时间至少1小时')
    .max(40, '每周可用时间不能超过40小时')
    .optional(),
  self_intro: z
    .string()
    .max(500, '自我介绍不能超过500字')
    .optional(),
  phone: z
    .union([
      z.literal(''),
      z.string().regex(/^1[3-9]\d{9}$/, '请填写有效的手机号'),
    ])
    .optional(),
}).superRefine((location, context) => {
  if (location.province_code.slice(0, 2) !== location.city_code.slice(0, 2)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['city_code'],
      message: '市级代码与所选省份不匹配',
    });
  }
  const districtMatchesCity = location.city_code.endsWith('0000')
    ? location.city_code.slice(0, 2) === location.district_code.slice(0, 2)
    : location.city_code.slice(0, 4) === location.district_code.slice(0, 4);
  if (!districtMatchesCity) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['district_code'],
      message: '区县代码与所选城市不匹配',
    });
  }
});

// ---- 评论 ----

export const createCommentSchema = z.object({
  content: z
    .string()
    .min(1, '评论内容不能为空')
    .max(1000, '评论内容不能超过1000字'),
  target_id: z.string().uuid('无效的目标 ID'),
  target_type: z.enum(['letter', 'museum_item'], { errorMap: () => ({ message: '无效的评论目标类型' }) }),
  parent_id: z.string().uuid().optional(),
  is_anonymous: z.boolean().optional(),
});

// ---- 漂流对话回复 ----

export const replyDriftSchema = z.object({
  threadId: z.string().uuid('无效的对话 ID'),
  content: z
    .string()
    .min(1, '回复内容不能为空')
    .max(2000, '回复内容不能超过2000字'),
});

// ---- OP Code 申请 ----

export const submitOpApplicationSchema = z.object({
  school_code: z
    .string()
    .length(2, '学校代码应为2位')
    .regex(/^[A-Z0-9]{2}$/, '学校代码应为2位大写字母或数字'),
  area_code: z
    .string()
    .length(2, '区域代码应为2位')
    .regex(/^[A-Z0-9]{2}$/, '区域代码应为2位大写字母或数字'),
  point_code: z
    .string()
    .length(2, '投递点代码应为2位')
    .regex(/^[A-Z0-9]{2}$/, '投递点代码应为2位大写字母或数字'),
  point_name: z
    .string()
    .min(2, '投递点名称至少2字')
    .max(50, '投递点名称不能超过50字')
    .optional(),
  reason: z
    .string()
    .max(500, '申请理由不能超过500字')
    .optional(),
});

// ---- Admin：系统数字设置 ----

export const oppSettingsNumberSchema = z.object({
  opp_daily_letter_limit: z
    .number()
    .int('必须为整数')
    .min(1, '每日上限至少为1')
    .max(100, '每日上限不能超过100'),
  opp_max_letter_length: z
    .number()
    .int('必须为整数')
    .min(100, '最大字数至少100字')
    .max(50000, '最大字数不能超过50000字'),
});

// ---- Admin：信件审核 reason ----

export const reviewLetterSchema = z.object({
  letterId: z.string().uuid('无效的信件 ID'),
  action: z.enum(['approve', 'reject', 'hold']),
  reason: z
    .string()
    .max(500, '审核理由不能超过500字')
    .optional(),
});

// 拒绝时 reason 必填
export const rejectLetterSchema = reviewLetterSchema.refine(
  (data) => data.action !== 'reject' || (data.reason?.trim().length ?? 0) >= 2,
  { message: '拒绝时必须填写理由（至少2字）', path: ['reason'] }
);

// ---- 发送信件 ----

const OP_CODE_REGEX = /^[A-Z0-9]{6}$/;

export const sendLetterSchema = z.object({
  content: z
    .string()
    .refine((value) => value.replace(/<[^>]*>/g, '').replace(/&(?:nbsp|#160|#xA0);/gi, ' ').trim().length >= MIN_LETTER_CONTENT_LENGTH, '请填写信件内容')
    .refine((value) => value.replace(/<[^>]*>/g, '').length <= MAX_LETTER_CONTENT_LENGTH, '信件内容不能超过50000字'),
  title: z
    .string()
    .max(MAX_LETTER_TITLE_LENGTH, '标题不能超过100字')
    .optional(),
  recipientCode: z
    .union([
      z.literal(''),
      z.string().regex(OP_CODE_REGEX, 'OP Code 格式不正确，应为6位大写字母或数字'),
    ])
    .optional(),
  scheduledDate: z
    .string()
    .refine(
      (val) => !val || val >= new Date(Date.now() + 86400000).toISOString().split('T')[0],
      '定时日期必须是明天或之后'
    )
    .optional(),
});
